import {
    ELEMENT_TYPE,
    CSSVariablesMap,
    variationClassMap,
    richTextStyleMap,
    FEATURE_FLAG_KEYS,
} from 'shop-ui/constants';
import { type UsePreviewUIStylePropTypes } from './usePreviewUIStyle';

export function getIconConfig(styleMap: UsePreviewUIStylePropTypes['style']) {
    if (
        typeof styleMap !== 'object' ||
        styleMap === null ||
        !styleMap[FEATURE_FLAG_KEYS.BUTTON_ICON] ||
        !styleMap[FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE] ||
        styleMap[FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE] === 'none'
    ) {
        return {
            iconClassName: '',
            iconPosition: '',
        };
    }

    return {
        iconClassName: styleMap[FEATURE_FLAG_KEYS.BUTTON_ICON_TYPE],
        iconPosition: styleMap[FEATURE_FLAG_KEYS.BUTTON_ICON_POSITION] || 'left',
    };
}

export function getCSSVariableValueMap(styleMap: UsePreviewUIStylePropTypes['style']) {
    if (typeof styleMap !== 'object' || styleMap === null) {
        return {};
    }

    const containerStyle = {};

    if (
        (!styleMap[FEATURE_FLAG_KEYS.BACKGROUND_TYPE] ||
            styleMap[FEATURE_FLAG_KEYS.BACKGROUND_TYPE] === 'color' ||
            styleMap[FEATURE_FLAG_KEYS.BACKGROUND_TYPE] === 'gradient') &&
        styleMap[FEATURE_FLAG_KEYS.BACKGROUND_COLOR]
    ) {
        containerStyle[CSSVariablesMap[FEATURE_FLAG_KEYS.BACKGROUND_COLOR]] =
            styleMap[FEATURE_FLAG_KEYS.BACKGROUND_COLOR];
    }

    if (styleMap[FEATURE_FLAG_KEYS.IMAGE_FIT]) {
        containerStyle[CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_FIT]] =
            styleMap[FEATURE_FLAG_KEYS.IMAGE_FIT];

        containerStyle['--shop-ui-custom-background-width'] =
            styleMap[FEATURE_FLAG_KEYS.IMAGE_FIT] === 'contain' ? 'unset' : '100%';

        containerStyle['--shop-ui-custom-background-height'] =
            styleMap[FEATURE_FLAG_KEYS.IMAGE_FIT] === 'contain' ? 'unset' : '100%';
    }

    if (styleMap[FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]) {
        containerStyle[
            CSSVariablesMap[FEATURE_FLAG_KEYS[FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT]]
        ] = styleMap[FEATURE_FLAG_KEYS.BUTTON_ALIGNMENT];
    }

    if (styleMap[FEATURE_FLAG_KEYS.MENU_ALIGNMENT] === 'right') {
        containerStyle[
            CSSVariablesMap[FEATURE_FLAG_KEYS[FEATURE_FLAG_KEYS.MENU_ALIGNMENT]]
        ] = 'flex-end';
    }

    if (styleMap[FEATURE_FLAG_KEYS.MENU_ALIGNMENT] === 'center') {
        containerStyle[
            CSSVariablesMap[FEATURE_FLAG_KEYS[FEATURE_FLAG_KEYS.MENU_ALIGNMENT]]
        ] = 'center';
    }

    if (styleMap[FEATURE_FLAG_KEYS.MENU_ALIGNMENT] === 'left') {
        containerStyle[
            CSSVariablesMap[FEATURE_FLAG_KEYS[FEATURE_FLAG_KEYS.MENU_ALIGNMENT]]
        ] = 'flex-start';
    }

    return Object.entries(styleMap).reduce(
        (acc, [key, value]) => {
            if (
                (key === FEATURE_FLAG_KEYS.HOVER_BACKGROUND_COLOR ||
                    key === FEATURE_FLAG_KEYS.HOVER_BORDER_COLOR ||
                    key === FEATURE_FLAG_KEYS.HOVER_BORDER_RADIUS ||
                    key === FEATURE_FLAG_KEYS.HOVER_FONT_COLOR ||
                    key === FEATURE_FLAG_KEYS.HOVER_FONT_STYLE) &&
                !styleMap[FEATURE_FLAG_KEYS.ENABLE_HOVER_STYLE]
            ) {
                return acc;
            }

            if (
                (key === FEATURE_FLAG_KEYS.ACTIVE_BACKGROUND_COLOR ||
                    key === FEATURE_FLAG_KEYS.ACTIVE_BORDER_COLOR ||
                    key === FEATURE_FLAG_KEYS.ACTIVE_BORDER_RADIUS ||
                    key === FEATURE_FLAG_KEYS.ACTIVE_FONT_COLOR ||
                    key === FEATURE_FLAG_KEYS.ACTIVE_FONT_STYLE) &&
                !styleMap[FEATURE_FLAG_KEYS.ENABLE_ACTIVE_STYLE]
            ) {
                return acc;
            }

            if (
                key === FEATURE_FLAG_KEYS.BACKGROUND_COLOR ||
                key === FEATURE_FLAG_KEYS.IMAGE_FIT ||
                key === FEATURE_FLAG_KEYS.IMAGE_POSITION
            ) {
                return acc;
            }

            if (styleMap[FEATURE_FLAG_KEYS.IMAGE_POSITION]) {
                if (styleMap[FEATURE_FLAG_KEYS.IMAGE_FIT] === 'contain') {
                    containerStyle[CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_POSITION]] =
                        'top';
                } else {
                    containerStyle[CSSVariablesMap[FEATURE_FLAG_KEYS.IMAGE_POSITION]] =
                        styleMap[FEATURE_FLAG_KEYS.IMAGE_POSITION];
                }
            }

            if (key === FEATURE_FLAG_KEYS.FONT_SIZE && value === 'custom') {
                acc[CSSVariablesMap[key]] =
                    (styleMap[FEATURE_FLAG_KEYS.FONT_SIZE_CUSTOM] || 12) + 'px';
            } else if (key === FEATURE_FLAG_KEYS.FIXED_SCREEN_HEIGHT && value) {
                acc[CSSVariablesMap[FEATURE_FLAG_KEYS.MIN_HEIGHT]] = '100vh';
            } else {
                // 沒有定義 CSS 變數不計算
                // 有值不重複負計算
                // type boolean 不計算
                if (
                    !CSSVariablesMap[key] ||
                    acc[CSSVariablesMap[key]] ||
                    typeof value === 'boolean'
                ) {
                    return acc;
                }

                if (key === FEATURE_FLAG_KEYS.BORDER_COLOR && value) {
                    acc['--shop-ui-custom-border-width'] = '1px';
                }

                if (key === FEATURE_FLAG_KEYS.HOVER_BORDER_COLOR && value) {
                    acc['--shop-ui-custom-hover-border-width'] = '1px';
                }

                if (key === FEATURE_FLAG_KEYS.ACTIVE_BORDER_COLOR && value) {
                    acc['--shop-ui-custom-active-border-width'] = '1px';
                }

                acc[CSSVariablesMap[key]] = value as string;
            }

            return acc;
        },
        containerStyle as Record<string, string>
    );
}

export function getBooleanStyleClassName(
    type: ELEMENT_TYPE,
    styleMap: UsePreviewUIStylePropTypes['style']
) {
    if (typeof styleMap !== 'object' || styleMap === null) {
        return {
            variation: '',
            className: '',
        };
    }

    return Object.entries(richTextStyleMap)
        .reduce((acc, [_key, className]) => {
            if (!styleMap[_key]) {
                return acc;
            }

            if (_key === FEATURE_FLAG_KEYS.FONT_SIZE) {
                acc.push(`${type}--${styleMap[_key]}`);
            } else {
                acc.push(`${type}--${className}`);
            }

            return acc;
        }, [] as string[])
        .join(' ');
}

export function getVariationClassName(
    type: ELEMENT_TYPE,
    styleMap: UsePreviewUIStylePropTypes['style']
) {
    if (typeof styleMap !== 'object' || styleMap === null) {
        return {
            variation: '',
            className: '',
        };
    }

    const key = variationClassMap[type];

    const variation = styleMap[key];

    if (key === FEATURE_FLAG_KEYS.ENABLE_SHADOW && variation) {
        return {
            variation: '',
            className: `${type}--shadow`,
        };
    }

    if (!key || !variation) {
        return {
            variation: '',
            className: '',
        };
    }

    return {
        variation,
        className: `${type}--${variation}`,
    };
}

export const __test__functionMap = {
    getCSSVariableValueMap,
    getVariationClassName,
};
